import React, { useState } from 'react';
import PublicService from '../../../../Services/public.service';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ExamFeedbackForm = () => {
    const [formData, setFormData] = useState({
        regNo: '',
        name: '',
        examStaffRating: 3,
        portalRating: 3,
        overallRating: 3,
        comments: ''
    });

    const [errors, setErrors] = useState<any>({});
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors: any = {};

        if (!formData.regNo.trim()) {
            newErrors.regNo = 'Registration Number is required';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting form with data:', formData);

        if (validateForm()) {
            setSubmitted(true);

            let payload: any = {
                regNo: formData.regNo,
                name: formData.name,
                examStaffRating: formData.examStaffRating,
                portalRating: formData.portalRating,
                overallRating: formData.overallRating,
            };

            if (formData.comments.trim()) {
                payload.comments = formData.comments;
            }

            await PublicService.submitExamFeedback(payload)
                .then(res => {
                    if (res.status === 200) {
                        setSubmitted(true);
                        toast.success('Feedback submitted successfully!');
                        handleReset();
                        navigate("/exam_submitted");
                    }
                })
                .catch(err => {
                    console.error('Error submitting feedback:', err);
                    toast.error(err?.response?.data?.message || err?.response?.data || 'Failed to submit feedback. Please try again later.');

                    setSubmitted(false);
                });
        }
    };

    const handleReset = () => {
        setFormData({
            regNo: '',
            name: '',
            examStaffRating: 3,
            portalRating: 3,
            overallRating: 3,
            comments: ''
        });
        setErrors({});
        setSubmitted(false);
    };

    const StarRating = ({ name, value, onChange }) => {
        return (
            <div className="d-flex align-items-center">
                {[1, 2, 3, 4, 5].map(star => (
                    <span
                        key={star}
                        className={`fs-4 me-1 ${star <= value ? 'text-warning' : 'text-muted'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onChange({ target: { name, value: star } })}
                    >
                        ★
                    </span>
                ))}
                <span className="ms-2 text-muted">({value}/5)</span>
            </div>
        );
    };

    if (submitted) {
        return (
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-8">
                        <div className="card border-success">
                            <div className="card-body text-center">
                                <div className="text-success mb-3">
                                    <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <h3 className="text-success">Thank You!</h3>
                                <p className="lead">Your feedback has been submitted successfully.</p>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleReset}
                                >
                                    Submit Another Feedback
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <div className="card shadow">
                        <div className="card-header bg-primary text-white">
                            <h3 className="card-title mb-0 text-center">
                                <i className="bi bi-clipboard-check me-2"></i>
                                Exam Feedback Form
                            </h3>
                        </div>
                        <div className="card-body">
                            <p className="text-muted mb-4">
                                Please provide your feedback about the examination process. Your input helps us improve the system.
                            </p>

                            <div onSubmit={handleSubmit}>
                                {/* Registration Number */}
                                <div className="mb-4">
                                    <label htmlFor="regNo" className="form-label fw-bold">
                                        Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                                        id="name"
                                        name="name"
                                        value={formData?.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your name"
                                    />
                                    {errors.name && (
                                        <div className="invalid-feedback">{errors.name}</div>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <label htmlFor="regNo" className="form-label fw-bold">
                                        Registration Number <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.regNo ? 'is-invalid' : ''}`}
                                        id="regNo"
                                        name="regNo"
                                        value={formData.regNo}
                                        onChange={handleInputChange}
                                        placeholder="Enter your registration number"
                                    />
                                    {errors.regNo && (
                                        <div className="invalid-feedback">{errors.regNo}</div>
                                    )}
                                </div>

                                {/* Exam Staff Rating */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold">
                                        Exam Staff Rating
                                    </label>
                                    <p className="text-muted small mb-2">
                                        Rate the helpfulness and professionalism of the examination staff
                                    </p>
                                    <StarRating
                                        name="examStaffRating"
                                        value={formData.examStaffRating}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Portal Rating */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold">
                                        Examination Portal Rating
                                    </label>
                                    <p className="text-muted small mb-2">
                                        Rate the user experience of the examination portal/platform
                                    </p>
                                    <StarRating
                                        name="portalRating"
                                        value={formData.portalRating}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Overall Rating */}
                                <div className="mb-4">
                                    <label className="form-label fw-bold">
                                        Overall Exam System Rating
                                    </label>
                                    <p className="text-muted small mb-2">
                                        Rate your overall satisfaction with the examination system
                                    </p>
                                    <StarRating
                                        name="overallRating"
                                        value={formData.overallRating}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                {/* Comments */}
                                <div className="mb-4">
                                    <label htmlFor="comments" className="form-label fw-bold">
                                        Additional Comments (Optional)
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="comments"
                                        name="comments"
                                        rows={4}
                                        value={formData.comments}
                                        onChange={handleInputChange}
                                        placeholder="Please share any specific feedback, suggestions, or concerns about the examination process..."
                                    ></textarea>
                                    <div className="form-text">
                                        Your detailed feedback helps us identify areas for improvement.
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary me-md-2"
                                        onClick={handleReset}
                                    >
                                        Reset Form
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className="btn btn-primary"
                                    >
                                        Submit Feedback
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExamFeedbackForm;